import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import buildStationObject from '@/helpers/build_station_object';
import safeRefreshStation from '@/helpers/refresh_station';

import useVpnStore from './vpn';

export const STATION_NUMBERS = [21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39];

const useStationsStore = defineStore(
  'stations',
  () => {
    const vpnStore = useVpnStore();
    const { stationOnline } = vpnStore;
    const { onlineClients: onlineVpnClients } = storeToRefs(vpnStore);

    const loading = ref(false);
    const stations = ref({});
    const selectedHosts = ref([]);
    const activeAudioHost = ref(null);

    const stationsList = computed(() => Object.values(stations.value));
    const selectedStationsList = computed(() =>
      selectedHosts.value.map((host) => stationsList.value.find((s) => s.host === host)).filter(Boolean),
    );

    watch(onlineVpnClients, async () => {
      const promises = [];

      for (const station of stationsList.value) {
        const online = stationOnline({ host: station.host });

        if (station.online !== online) {
          station.online = online;

          if (online) {
            promises.push(
              safeRefreshStation({ host: station.host }).then(
                (freshData) => (stations.value[station.host] = freshData),
              ),
            );
          }
        }
      }
    });

    async function refreshOne({ host, silent = false } = {}) {
      if (!stations.value[host]) stations.value[host] = buildStationObject({ host, status: 'loading' });
      if (!silent) stations.value[host].status = 'loading';
      const freshData = await safeRefreshStation({ host });
      stations.value[host] = freshData;
      return { host, status: freshData.status };
    }

    async function refreshAll({ silent = false } = {}) {
      if (!silent) loading.value = true;

      const hosts = Array.from(
        new Set(Object.keys(stations.value).concat(STATION_NUMBERS.map((n) => `10.210.${n}.2`))),
      );

      const results = await Promise.all(hosts.map((host) => refreshOne({ host, silent })));

      const ok = results.filter((r) => r.status === 'ok').map((r) => r.host);
      const failed = results.filter((r) => r.status !== 'ok');
      console.groupCollapsed(`[auto-refresh] ${new Date().toLocaleTimeString()}`);
      if (ok.length) console.log('ok', ok);
      if (failed.length)
        console.log(
          'failed',
          failed.map((r) => `${r.host} (${r.status})`),
        );
      console.groupEnd();

      if (!silent) loading.value = false;
    }

    async function addStation(host) {
      if (!host) return;

      if (!stations.value[host]) {
        stations.value[host] = buildStationObject({ host, status: 'loading' });
      }

      if (!selectedHosts.value.includes(host)) {
        selectedHosts.value.push(host);
      }

      await refreshOne({ host });
    }

    function selectStations(hosts) {
      selectedHosts.value = hosts;
    }

    function unselectStation(host) {
      selectedHosts.value = selectedHosts.value.filter((h) => h !== host);
    }

    return {
      loading,
      stations,
      selectedHosts,

      stationsList,
      selectedStationsList,

      activeAudioHost,

      refreshOne,
      refreshAll,
      addStation,
      selectStations,
      unselectStation,
    };
  },
  {
    persist: { debug: true, omit: ['activeAudioHost'] },
  },
);

export default useStationsStore;
